import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { letterheadsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Settings as SettingsIcon, DollarSign, Save, Truck, FileImage, Upload, Trash2, Plus } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [letterheads, setLetterheads] = useState([]);
  const [newLetterheadName, setNewLetterheadName] = useState("");
  const [uploadingLetterhead, setUploadingLetterhead] = useState(false);
  const letterheadFileRef = useRef(null);
  
  // Revenue per trip settings based on distance ranges
  const [revenueRates, setRevenueRates] = useState({
    "0-10": "",
    "10-15": "",
    "15-20": "",
    "20-25": "",
    "25-30": "",
    "30+": "",
  });

  const isAdmin = user?.role === "admin";

  const distanceRanges = [
    { key: "0-10", label: "0 - 10 KM" },
    { key: "10-15", label: "10 - 15 KM" },
    { key: "15-20", label: "15 - 20 KM" },
    { key: "20-25", label: "20 - 25 KM" },
    { key: "25-30", label: "25 - 30 KM" },
    { key: "30+", label: "30 KM and above" },
  ];

  useEffect(() => {
    fetchSettings();
    fetchLetterheads();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.revenue_rates) {
          setRevenueRates(data.revenue_rates);
        }
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLetterheads = async () => {
    try {
      const response = await letterheadsAPI.getAll();
      setLetterheads(response.data);
    } catch (error) {
      console.error("Failed to fetch letterheads:", error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_URL}/api/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          revenue_rates: revenueRates,
        }),
      });

      if (response.ok) {
        toast.success("Settings saved successfully");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRateChange = (key, value) => {
    // Only allow numbers and decimal point
    const sanitized = value.replace(/[^0-9.]/g, "");
    setRevenueRates((prev) => ({
      ...prev,
      [key]: sanitized,
    }));
  };

  const handleLetterheadUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!newLetterheadName.trim()) {
      toast.error("Please enter a name for the letterhead first");
      return;
    }

    try {
      setUploadingLetterhead(true);
      await letterheadsAPI.upload(newLetterheadName.trim(), file);
      toast.success("Letterhead uploaded successfully");
      setNewLetterheadName("");
      fetchLetterheads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload letterhead");
    } finally {
      setUploadingLetterhead(false);
      if (letterheadFileRef.current) {
        letterheadFileRef.current.value = "";
      }
    }
  };

  const handleDeleteLetterhead = async (id) => {
    try {
      await letterheadsAPI.delete(id);
      toast.success("Letterhead deleted");
      fetchLetterheads();
    } catch (error) {
      toast.error("Failed to delete letterhead");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <SettingsIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Only administrators can access settings</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Settings
          </h1>
          <p className="text-slate-500">Configure system settings, rates, and letterheads</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider rounded-sm"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Letterhead Management */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <FileImage className="w-5 h-5 text-blue-600" />
            Letterheads
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-slate-500 mb-6">
            Upload company letterheads to display at the top of PDR Forms and Job Orders.
            Supported formats: PNG, JPG, JPEG. Max size: 2MB.
          </p>
          
          {/* Upload New Letterhead */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6 p-4 bg-slate-50 rounded-sm border border-slate-200">
            <div className="flex-1 space-y-2">
              <Label className="font-semibold uppercase text-xs tracking-wide">Letterhead Name</Label>
              <Input
                placeholder="e.g., Poh Li Heng"
                value={newLetterheadName}
                onChange={(e) => setNewLetterheadName(e.target.value)}
                className="rounded-sm"
                data-testid="letterhead-name-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={() => letterheadFileRef.current?.click()}
                disabled={uploadingLetterhead || !newLetterheadName.trim()}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm"
                data-testid="upload-letterhead-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingLetterhead ? "Uploading..." : "Upload Image"}
              </Button>
              <input
                type="file"
                ref={letterheadFileRef}
                onChange={handleLetterheadUpload}
                accept="image/*"
                className="hidden"
              />
            </div>
          </div>

          {/* Letterhead List */}
          {letterheads.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-300 rounded-sm">
              <FileImage className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No letterheads uploaded yet</p>
              <p className="text-sm text-slate-400">Upload your first letterhead above</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {letterheads.map((lh) => (
                <div key={lh.id} className="border border-slate-200 rounded-sm overflow-hidden">
                  <div className="h-24 bg-white flex items-center justify-center p-2 border-b border-slate-100">
                    <img
                      src={lh.image_data}
                      alt={lh.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="p-3 bg-slate-50 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-slate-800">{lh.name}</p>
                      <p className="text-xs text-slate-500">ID: {lh.id}</p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-letterhead-${lh.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Letterhead?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete the "{lh.name}" letterhead. Forms using this letterhead will no longer display it.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteLetterhead(lh.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue Per Trip Settings */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Revenue Per Trip
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-slate-500 mb-6">
            Set the revenue amount for each delivery based on the distance traveled. 
            These rates will be used to calculate the "Revenue Per Trip" in the Financials report.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {distanceRanges.map((range) => (
              <div key={range.key} className="space-y-2">
                <Label className="font-semibold uppercase text-xs tracking-wide flex items-center gap-2">
                  <Truck className="w-4 h-4 text-slate-400" />
                  {range.label}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">
                    $
                  </span>
                  <Input
                    type="text"
                    value={revenueRates[range.key] || ""}
                    onChange={(e) => handleRateChange(range.key, e.target.value)}
                    placeholder="0.00"
                    className="pl-8 rounded-sm"
                    data-testid={`rate-${range.key}`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-sm">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Leave a field empty or set to 0 if no revenue should be calculated for that distance range.
              The revenue is calculated based on the actual distance traveled during the delivery (check-in to check-out GPS coordinates).
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
